import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import PlatFormAPIAxiosInstance from "../utils/platFormAxios";

export interface Worker {
  id: string;
  name: string;
}

export interface AssigneeOption {
  value: string;
  label: string;
}

/**
 * Stable fallback so consumers defaulting `data` keep a constant reference
 * across renders (safe to use as an effect dependency).
 */
export const EMPTY_ASSIGNEES: AssigneeOption[] = [];

const toAssigneeOptions = (workers: Worker[]): AssigneeOption[] =>
  [...workers]
    .sort((a, b) => {
      const nameA = a?.name ? a.name.toLowerCase() : "";
      const nameB = b?.name ? b.name.toLowerCase() : "";
      if (nameA < nameB) return -1;
      if (nameA > nameB) return 1;
      return 0;
    })
    .map((worker) => ({ value: worker?.id, label: worker?.name }));

/**
 * to get the workers (assignees) of a property, ready to be used as
 * select options in the create/update trial form
 * @param orgId The organization the property belongs to.
 * @param propertyId The property (farm) whose workers should be fetched.
 * @returns The sorted assignee options for the given property.
 */
export const useWorkers = (
  orgId?: string | null,
  propertyId?: string | null
): UseQueryResult<AssigneeOption[]> => {
  return useQuery({
    queryKey: ["workers", orgId, propertyId],
    enabled: !!orgId && !!propertyId,
    queryFn: async (): Promise<AssigneeOption[]> => {
      const response = await PlatFormAPIAxiosInstance.post(
        `/orgs/${orgId ?? ""}/properties/ids/workers`,
        { ids: [propertyId ?? ""] }
      );
      const content: Worker[] = response?.data?.content ?? [];
      return toAssigneeOptions(content);
    },
  });
};

export default useWorkers;
